/**
 * HTML Sanitizer for Email Templates
 *
 * Sanitizes template HTML before save and before send:
 *   - Strips <script>, <style>, <iframe>, <object>, <embed>, <form> tags
 *   - Removes event handler attributes (onclick, onerror, onload, etc.)
 *   - Restricts link href protocols to http, https, mailto
 *   - Preserves safe email tags: p, br, strong, em, u, a, ul, ol, li,
 *     h1/h2/h3, table/tr/td/th/thead/tbody, div, span, img (src only)
 *   - Escapes dynamic variable values before insertion
 */

// Allowlisted tags for email HTML (used for documentation; stripping logic uses patterns)
// p, br, strong, b, em, i, u, s, a, ul, ol, li, h1-h6,
// table, thead, tbody, tfoot, tr, td, th, div, span, img, hr, blockquote, pre, code

const DANGEROUS_TAG_PATTERN = /<\s*\/?\s*(script|style|iframe|object|embed|form|input|applet|meta|link|base)\b[^>]*>/gi;

const EVENT_HANDLER_PATTERN = /\s+on\w+\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]*)/gi;

const JAVASCRIPT_PROTOCOL_PATTERN = /\s+href\s*=\s*(?:"javascript:[^"]*"|'javascript:[^']*')/gi;
const DATA_PROTOCOL_HREF_PATTERN = /\s+href\s*=\s*(?:"data:[^"]*"|'data:[^']*')/gi;
const JAVASCRIPT_SRC_PATTERN = /\s+src\s*=\s*(?:"javascript:[^"]*"|'javascript:[^']*')/gi;
const DATA_SRC_NON_IMAGE_PATTERN = /\s+src\s*=\s*(?:"data:(?!image\/)[^"]*"|'data:(?!image\/)[^']*')/gi;

/**
 * Sanitize email template HTML body.
 * Removes dangerous elements and attributes while preserving safe formatting.
 */
export function sanitizeHtml(html: string): string {
  if (!html) return html;

  let sanitized = html;

  sanitized = sanitized.replace(DANGEROUS_TAG_PATTERN, '');

  sanitized = sanitized.replace(EVENT_HANDLER_PATTERN, '');

  sanitized = sanitized.replace(JAVASCRIPT_PROTOCOL_PATTERN, ' href="#"');
  sanitized = sanitized.replace(DATA_PROTOCOL_HREF_PATTERN, ' href="#"');
  sanitized = sanitized.replace(JAVASCRIPT_SRC_PATTERN, ' src=""');
  sanitized = sanitized.replace(DATA_SRC_NON_IMAGE_PATTERN, ' src=""');

  return sanitized;
}

/**
 * Escape a dynamic value before inserting into HTML body.
 * Prevents XSS from user-controlled data like patient names.
 */
export function escapeHtmlValue(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Check if HTML contains unsafe content and return issues.
 * Used for validation warnings in the template editor.
 */
export interface HtmlSanitizationIssue {
  type: 'error' | 'warning';
  message: string;
}

export function validateHtmlSafety(html: string): HtmlSanitizationIssue[] {
  const issues: HtmlSanitizationIssue[] = [];

  if (DANGEROUS_TAG_PATTERN.test(html)) {
    DANGEROUS_TAG_PATTERN.lastIndex = 0;
    issues.push({ type: 'error', message: 'Template contains unsafe tags (script, style, iframe, form, input, etc.). Remove them before saving.' });
  }

  if (EVENT_HANDLER_PATTERN.test(html)) {
    EVENT_HANDLER_PATTERN.lastIndex = 0;
    issues.push({ type: 'error', message: 'Template contains event handler attributes (onclick, onerror, etc.). Remove them before saving.' });
  }

  if (JAVASCRIPT_PROTOCOL_PATTERN.test(html)) {
    JAVASCRIPT_PROTOCOL_PATTERN.lastIndex = 0;
    issues.push({ type: 'error', message: 'Template contains javascript: URLs. Use https: or mailto: links instead.' });
  }

  if (DATA_PROTOCOL_HREF_PATTERN.test(html)) {
    DATA_PROTOCOL_HREF_PATTERN.lastIndex = 0;
    issues.push({ type: 'error', message: 'Template contains data: URLs in links. Use https: or mailto: links instead.' });
  }

  return issues;
}

/**
 * Returns true if the HTML is safe (no dangerous patterns found).
 */
export function isHtmlSafe(html: string): boolean {
  return validateHtmlSafety(html).length === 0;
}
